'use client'

import { useEffect, useState } from 'react'
import { signIn } from 'next-auth/react'
import { toast } from '@/lib/toast'
import { useRouter, useSearchParams } from 'next/navigation'

import Input from '../elements/Input'
import { Button } from '../elements/Button'
import { CircleCheckBig, Eye, EyeOff, KeyRound, MoveLeft, MoveRight, RotateCcw, ScanFace, Shield } from 'lucide-react'
import { sendLoginOTP, verifyLoginOTP } from '@/lib/request'

import { motion } from 'framer-motion'
import { REGEXP_ONLY_DIGITS } from "input-otp"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"

const OTP_COOLDOWN_SECONDS = 120

export default function Login() {
  const [emailPhone, setEmailPhone] = useState('')
  const [password, setPassword] = useState('')
  const [tab, setTab] = useState<'password' | 'otp'>('password')
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState(1)

  const [otp, setOTP] = useState('')
  const [sentOTP, setSentOTP] = useState(false)

  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [sendOTPLoading, setSendOTPLoading] = useState(false)
  const [isOTPLoading, setOTPLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [isCooldownActive, setIsCooldownActive] = useState(false)

  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl')

  useEffect(() => {
    if(otp && otp.length === 6) handleOTP()
  }, [otp])

  useEffect(() => {
    if(searchParams.has('error')) {
      setLoading(false)
      setGoogleLoading(false)
      if(searchParams.get('error') == 'notFound') toast.error('Бүртгэл олдсонгүй')
      if(searchParams.get('error') == 'serverError') toast.error('Сервертэй холбогдоход алдаа гарлаа')
    }
  }, [searchParams])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isCooldownActive && cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            setIsCooldownActive(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isCooldownActive, cooldownSeconds])

  const handleEmailPhoneChange = (event: any) => setEmailPhone(event.target.value)
  const handlePasswordChange = (event: any) => setPassword(event.target.value)
  const handleOTPChange = (value: any) => setOTP(value)

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      if(tab == 'password') handleLogin(event)
      else if(tab == 'otp') sendOTP()
    }
  }

  const signInWithGoogle = async () => {
    setGoogleLoading(true)
    if(callbackUrl) return await signIn('google', { callbackUrl: callbackUrl })
    await signIn('google')
  }

  const startCooldown = () => {
    setCooldownSeconds(OTP_COOLDOWN_SECONDS)
    setIsCooldownActive(true)
  }

  // NextAuth v5 only returns an error *type* plus our `code`, which is where
  // auth.ts stuffs the backend's Mongolian message. See auth.ts.
  const errorMessage = (response: any, fallback: string) => response?.code || fallback

  const handleLogin = async (e: any) => {
    e.preventDefault()

    if(!emailPhone) return toast.error('Имэйл эсвэл утасны дугаар хоосон байна')
    if(!password) return toast.error('Нууц үг хоосон байна')
    setLoading(true)
    const response = await signIn('credentials', {
      emailPhone,
      password,
      redirect: false
    })
    if(!response || response.ok === false || response.error) {
      setLoading(false)
      return toast.error(errorMessage(response, 'Нэвтрэхэд алдаа гарлаа'))
    }
    toast.success('Амжилттай нэвтэрлээ 🥳')
    setTimeout(() => {
      if(callbackUrl) router.push(callbackUrl)
      else router.push('/dashboard')
    }, 100)
  }

  const sendOTP = async () => {
    if(!emailPhone) return toast.error('Имэйл эсвэл утасны дугаар хоосон байна')
    setSendOTPLoading(true)
    const { error } = await sendLoginOTP({ emailPhone })
    setSendOTPLoading(false)
    if(error) return toast.error(error)
    toast.success('Код амжилттай илгээгдлээ ✉️')
    setSentOTP(true)
    setTab('otp')
    setStep(2)
    startCooldown()
  }

  const resendOTP = async () => {
    if(isCooldownActive) return
    if(!emailPhone) return toast.error('Имэйл эсвэл утасны дугаар хоосон байна')
    setOTP('')
    setResendLoading(true)
    const { error } = await sendLoginOTP({ emailPhone })
    setResendLoading(false)
    if(error) return toast.error(error)
    toast.success('Код дахин илгээгдлээ ✉️')
    setSentOTP(true)
    setTab('otp')
    setStep(2)
    startCooldown()
  }

  const handleOTP = async () => {
    if(!emailPhone) return toast.error('Имэйл эсвэл утасны дугаар хоосон байна')
    if(!otp) return toast.error('Баталгаажуулах код хоосон байна')
    setOTPLoading(true)
    const { data: verification, error } = await verifyLoginOTP({ code: otp, emailPhone })
    if(error || !verification) {
      setOTPLoading(false)
      return toast.error(error || 'Баталгаажуулах код буруу байна')
    }
    const response = await signIn('credentials', {
      verificationId: verification.id,
      code: verification.code,
      redirect: false
    })
    if(!response || response.ok === false || response.error) {
      setOTPLoading(false)
      return toast.error(errorMessage(response, 'Нэвтрэхэд алдаа гарлаа'))
    }
    toast.success('Амжилттай нэвтэрлээ 🥳')
    setTimeout(() => {
      if(callbackUrl) router.push(callbackUrl)
      else router.push('/dashboard')
    }, 100)
  }

  const routeToRegister = () => {
    if(callbackUrl) router.push(`/register?callbackUrl=${callbackUrl}`)
    else router.push('/register')
  }

  const routeToForgot = () => {
    if(callbackUrl) router.push(`/forgot?callbackUrl=${callbackUrl}`)
    else router.push('/forgot')
  }

  const reset = () => {
    setOTP('')
    setSentOTP(false)
    setStep(1)
    setCooldownSeconds(0)
    setIsCooldownActive(false)
  }

  return (
    <div className="w-full max-w-[380px]">
      <div className="text-center flex flex-col items-center mb-4">
        <h2 className="text-2xl font-semibold leading-9 font-gilroy text-foreground">
          Системд нэвтрэх
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Домэйн нэрээ удирдахын тулд нэвтэрнэ үү</p>
      </div>
      { (step == 2 && sentOTP) ? (
        <div>
          <div>
            <div className="text-center flex flex-col items-center gap-2 max-w-xs mx-auto">
              <motion.div className='text-sm font-medium text-muted-foreground'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Таны <span className='font-semibold text-foreground'>{ emailPhone }</span> руу илгээгдсэн 6 оронтой кодыг оруулна уу.
              </motion.div>
            </div>
          </div>
          <div className="space-y-4 grid mt-6">
            <motion.div className='flex justify-center'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {/* @ts-ignore */}
              <InputOTP value={otp} onChange={handleOTPChange} autoFocus={true} maxLength={6} pattern={REGEXP_ONLY_DIGITS} autoComplete="false">
                {/* @ts-ignore */}
                <InputOTPGroup>
                  {/* @ts-ignore */}
                  <InputOTPSlot index={0} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={1} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={2} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={3} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={4} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={5} showChar={true} />
                </InputOTPGroup>
              </InputOTP>
            </motion.div>

            <motion.div className='col-span-full flex flex-col gap-4'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <Button type="button" onClick={handleOTP} icon={CircleCheckBig} isLoading={isOTPLoading} className="w-full" loadingMessage="Баталгаажуулж байна...">
                Баталгаажуулах
              </Button>
              <div className='grid grid-cols-2 gap-2'>
                <Button type="button" onClick={reset} icon={MoveLeft} variant="ghost" className="w-full">
                  Буцах
                </Button>
                <Button
                  type="button"
                  onClick={resendOTP}
                  icon={RotateCcw}
                  isLoading={resendLoading}
                  variant="ghost"
                  className="w-full"
                  loadingMessage="Илгээж байна..."
                  disabled={isCooldownActive}
                >
                  {isCooldownActive ? `${cooldownSeconds} секунд` : 'Дахин илгээх'}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        <div>
          <div className="space-y-3 grid grid-cols-2">
            <Input value={emailPhone} label='Утасны дугаар / Имэйл' type='text' placeholder='Утасны дугаар эсвэл имэйл хаяг' onChange={handleEmailPhoneChange} onKeyDown={handleKeyDown} required autoFocus container="col-span-full !max-w-none" />
            { tab == 'password' ? (
              <>
                <div className='col-span-full relative'>
                  <Input value={password} label='Нууц үг' type={showPassword ? 'text' : 'password'} rightIcon={showPassword ? EyeOff : Eye} rightAction={() => setShowPassword(p => !p)} placeholder='Таны нэвтрэх нууц үг' onChange={handlePasswordChange} onKeyDown={handleKeyDown} required container="w-full !max-w-none" />
                  <div className="text-xs leading-6 absolute -top-1 right-0">
                    <div onClick={routeToForgot} className="font-medium text-muted-foreground hover:text-foreground transition cursor-pointer">
                      Нууц үгээ мартсан уу?
                    </div>
                  </div>
                </div>

                <div className='col-span-full'>
                  <Button type="button" onClick={handleLogin} isLoading={loading} icon={ScanFace} className="w-full" loadingMessage="Нэвтэрч байна...">
                    Нэвтрэх
                  </Button>
                </div>
              </>
            ) : (
              <div className='col-span-full'>
                <Button type="button" onClick={sendOTP} isLoading={sendOTPLoading} icon={MoveRight} iconPosition='right' className="w-full" loadingMessage="Илгээж байна...">
                  Код илгээх
                </Button>
              </div>
            ) }
          </div>

          <div onClick={routeToRegister} className="cursor-pointer mb-4 text-sm leading-6 text-muted-foreground text-center mt-3 py-1 font-medium bg-muted rounded-lg">
            Шинэ хэрэглэгч үү?{' '}
            <span className="font-semibold text-gradient ml-1">
              Бүртгэл үүсгэх
            </span>
          </div>

          <div className="relative mt-3">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm font-medium leading-6">
              <span className="bg-pure px-6 text-muted-foreground">Эсвэл</span>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2">
            { tab == 'password' && <Button onClick={() => setTab('otp')} variant="secondary" width="full" icon={Shield}>Нэг удаагийн кодоор нэвтрэх</Button> }
            { tab == 'otp' && <Button onClick={() => setTab('password')} variant="secondary" width="full" icon={KeyRound}>Нууц үгээр нэвтрэх</Button> }
            <Button onClick={signInWithGoogle} loadingMessage="Нэвтэрч байна..." isLoading={googleLoading} variant="secondary" width="full">
              <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
                <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
                <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
                <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
                <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853" />
              </svg>
              <span className="text-sm font-semibold leading-6">Google ашиглах</span>
            </Button>
          </div>
        </div>
      ) }
    </div>
  )
}
